/**
 * ── A CARRIER'S OWN TRACKING PAGE, FROM AMAZON'S `carrierCode` ────────────────────────────────
 *
 * The Shopify lane gets a `trackingUrl` handed to it by Shopify. Amazon MCF does not: a
 * `fulfillmentShipmentPackage` carries a `carrierCode` and a `trackingNumber` and nothing else, so
 * the link has to be built here.
 *
 * WHAT THE NUMBER IS. The **Carrier Tracking Number** (requester, 2026-09-17) — not the Amazon
 * Fulfillment Tracking Number, which Amazon's own Seller Central note says is a valid tracking
 * number only on TikTok Shop. `trackingNumber` on the package is the carrier's; AFTN is the
 * separate `amazonFulfillmentTrackingNumber` field, which this feature does not read.
 *
 * ⚠️ NO LINK IS BETTER THAN A WRONG ONE, which is the whole shape of this file. An unrecognised
 * carrier returns undefined and the caller renders the number as plain text — still copyable,
 * still correct. A guessed URL, by contrast, gets pasted to a customer who then sees "not found"
 * and concludes their order never shipped.
 *
 * ── ONE CODE IS NOW OBSERVED, THE REST ARE NOT ────────────────────────────────────────────────
 *
 * ✅ `Amazon Logistics` — seen on a real shipment, 2026-09-17, alongside carrier tracking number
 * `TBA33…`. Note the SHAPE: a human-readable name with a space, not the `AMZN_US` token this map
 * was originally written against. The `AMAZON` prefix catches it, which is the reason matching is
 * loose rather than an equality check — an equality map keyed on `AMZN_US` would have missed the
 * first real carrier this lane ever reported.
 *
 * ⚠️ EVERY OTHER CODE IS STILL A GUESS, built from the carriers' documented public tracking URLs.
 * qa cannot help: the SP-API sandbox returns `fulfillmentShipments: []` forever. Check a new code
 * against this map the first time it appears rather than assuming it matched.
 *
 * ── THE swiship HOST IS REGIONAL, AND THE MARKETPLACE PICKS IT ────────────────────────────────
 *
 * `swiship.com` is the US host only. A German shipment tracks on `swiship.de` (requester,
 * 2026-09-21) — the same `id`, a different host, and the wrong host does not fall back: it
 * answers "not found" for a parcel that is moving normally.
 *
 * THE KEY IS `mcfMarketplace`, NOT `shipToCountry`, and the two genuinely differ. Amazon DE
 * fulfils all 27 EU destinations, so a request shipping to France is handled by DE logistics and
 * tracks on `swiship.de`. The host follows the warehouse the parcel left, not the doormat it is
 * going to. Keying this on the destination would break every EU country except Germany.
 *
 * ⚠️ `AMAZON_UK` -> `swiship.co.uk` is the one the requester did NOT state. It is Amazon's
 * documented UK host and the obvious third member of a pattern whose other two are confirmed, but
 * nobody has watched a UK parcel resolve on it. The alternative was leaving UK on `.com`, which
 * is equally unverified and known to be the wrong shape. Check it the first time a real UK
 * shipment reports a number.
 *
 * NO MARKETPLACE -> `swiship.com`. That is the pre-2026-09-21 behaviour, kept for the China lane
 * and for a row whose marketplace was never recorded. It is a guess in exactly the way this file
 * says not to guess — but the alternative is dropping a link we used to render, and `.com` is at
 * least the right tracker family. An unknown-marketplace Amazon parcel is the one case here worth
 * revisiting if it ever shows up in anger.
 *
 * A separate consumer tracker at `track.amazon.com/tracking/<id>` also handles `TBA` numbers and
 * is region-free; it is the fallback to reach for if the regional hosts disappoint.
 *
 * Matching is deliberately loose — `startsWith` on a normalised code — because Amazon returns
 * variants (`AMZN_US`, `AMZN_UK`, `UPS Ground`) rather than one token per carrier.
 */

/**
 * `AMAZON_DE` -> `https://swiship.de`. The marketplace arrives already normalised (`AMAZONDE`).
 *
 * Unknown or absent falls to `.com`, which is the US host and the behaviour every link had before
 * regional hosts existed. Suffix-matched rather than equality-matched for the same reason the
 * carrier map is: Amazon has already shipped us one value in a shape nobody predicted.
 */
function swishipHost(marketplace: string): string {
  if (marketplace.endsWith('DE')) return 'https://swiship.de';
  if (marketplace.endsWith('UK') || marketplace.endsWith('GB')) return 'https://swiship.co.uk';
  return 'https://swiship.com';
}

/** `AMZN_US` / `ups ground` / `Royal-Mail` → `AMZNUS` / `UPSGROUND` / `ROYALMAIL`. */
function normalize(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Prefix → URL builder. ORDER MATTERS: the first prefix that matches wins, so anything that is a
 * prefix of another entry must come after it.
 */
const CARRIERS: ReadonlyArray<{
  prefix: string;
  name: string;
  url: (n: string, marketplace: string) => string;
}> = [
  // Amazon's own logistics. `swiship` is the tracker Amazon serves MCF shipments through, and it
  // is keyed on the tracking number rather than on any account id — but the HOST is regional.
  { prefix: 'AMZN', name: 'Amazon Logistics', url: (n, m) => `${swishipHost(m)}/track?id=${n}` },
  { prefix: 'AMAZON', name: 'Amazon Logistics', url: (n, m) => `${swishipHost(m)}/track?id=${n}` },
  { prefix: 'UPS', name: 'UPS', url: (n) => `https://www.ups.com/track?tracknum=${n}` },
  { prefix: 'USPS', name: 'USPS', url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}` },
  { prefix: 'FEDEX', name: 'FedEx', url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${n}` },
  // DHL runs several units under one code; this is the global tracker, which resolves across them.
  { prefix: 'DHL', name: 'DHL', url: (n) => `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${n}` },
  { prefix: 'ROYALMAIL', name: 'Royal Mail', url: (n) => `https://www.royalmail.com/track-your-item#/tracking-results/${n}` },
  { prefix: 'DPD', name: 'DPD', url: (n) => `https://track.dpd.co.uk/search?reference=${n}` },
  { prefix: 'GLS', name: 'GLS', url: (n) => `https://gls-group.eu/EU/en/parcel-tracking?match=${n}` },
  { prefix: 'HERMES', name: 'Hermes', url: (n) => `https://www.evri.com/track/parcel/${n}` },
  { prefix: 'EVRI', name: 'Evri', url: (n) => `https://www.evri.com/track/parcel/${n}` },
];

/** The carrier's human name, when the code is one we recognise. */
export function carrierName(carrierCode: string | null | undefined): string | undefined {
  const code = normalize(carrierCode ?? '');
  if (!code) return undefined;
  return CARRIERS.find((c) => code.startsWith(c.prefix))?.name;
}

/**
 * The carrier's tracking page for this number, or undefined when we cannot build one honestly.
 *
 * Undefined for an unknown carrier, for a missing number, and for a number carrying characters
 * that have no business in one — the last because a tracking number is interpolated into a URL,
 * and anything outside `[A-Za-z0-9-]` is either not a tracking number or an attempt to steer the
 * link somewhere else.
 */
export function carrierTrackingUrl(
  carrierCode: string | null | undefined,
  trackingNumber: string | null | undefined,
  mcfMarketplace?: string | null,
): string | undefined {
  const number = (trackingNumber ?? '').trim();
  if (!number || !/^[A-Za-z0-9-]+$/.test(number)) return undefined;
  const code = normalize(carrierCode ?? '');
  if (!code) return undefined;
  const carrier = CARRIERS.find((c) => code.startsWith(c.prefix));
  return carrier
    ? carrier.url(encodeURIComponent(number), normalize(mcfMarketplace ?? ''))
    : undefined;
}
